import { CartGetItem } from 'src/types';

export const AMAZON_MAX_PARAMS_PER_CHUNK = 25;

/**
 * Controls the Amazon chunking UX. Set via the hardcoded {@link AMAZON_CHUNK_VARIANT}
 * constant below — no longer driven by the `amazon-chunking` PostHog flag. Regardless
 * of this value, chunking can always be forced via the `enable_chunk` URL param or an
 * explicit `forceChunk` (e.g. the banner CTA).
 *
 * - 'disabled' — no banner and no auto-chunking; chunk only via `enable_chunk`/`forceChunk`.
 * - 'modal-only' — auto-chunks eligible carts on "Continue with X items", no banner.
 * - 'modal-and-banner' — shows the banner; chunking runs when the user clicks its CTA,
 *   not on plain "Continue with X items".
 */
export type AmazonChunkVariant = 'disabled' | 'modal-only' | 'modal-and-banner';

export const AMAZON_CHUNK_VARIANT: AmazonChunkVariant = 'modal-and-banner';

/**
 * Amazon Local Market / Fresh carts submit via a POST form (JSON ingredients
 * payload), not a length-limited add-to-cart URL, so chunking never applies to
 * them. Used to exclude these stores from the chunking banner and auto-chunking.
 */
export const isAmazonLocalCart = (store?: string): boolean =>
  !!store && (store.endsWith('/cart/localmarket') || store.endsWith('/cart/fresh'));

/** Total number of URL param entries needed to represent these items. Items with quantity > 10 require multiple entries (max 10 units each). */
export const getAmazonParamCount = (items: CartGetItem[]): number =>
  items.reduce((sum, item) => sum + Math.ceil(item.quantity / 10), 0);

/** Returns true if the cart would require chunking when sent to Amazon. */
export const willAmazonCartChunk = (items: CartGetItem[]): boolean =>
  getAmazonParamCount(items) > AMAZON_MAX_PARAMS_PER_CHUNK;
